import { errorBodySchema, healthSchema, type Health } from '@crm/contracts';
import { z } from 'zod';

/**
 * The browser's only door to the API. Every call names the Zod schema of the answer it
 * expects, so an off-contract response is a typed failure here rather than `undefined`
 * three components later. Network failures, non-2xx bodies and a 2xx that is not JSON are
 * all turned into `ApiError` — the same three cases Relay's `fetchJson` learned to handle.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    /** The API's request id, when the failure came from the API — quote it in a support thread. */
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: { accept: 'application/json', ...init?.headers },
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'Network error', 0, 'network_error');
  }
  const text = await res.text();
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (err) {
    throw new ApiError(
      `Non-JSON response (${res.status}): ${err instanceof Error ? err.message : 'parse error'}`,
      res.status,
      'non_json_response',
    );
  }
  if (!res.ok) {
    const body = errorBodySchema.safeParse(json);
    throw body.success
      ? new ApiError(
          body.data.error.message,
          res.status,
          body.data.error.code,
          body.data.request_id,
          body.data.error.details,
        )
      : new ApiError(`HTTP ${res.status}`, res.status, 'http_error');
  }
  const parsed = schema.safeParse(json);
  if (!parsed.success)
    throw new ApiError('Response did not match its contract', res.status, 'contract_mismatch');
  return parsed.data;
}

/** Human-readable text for an error surfaced in the UI. Always carries the code when there is one. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.code})`;
  return err instanceof Error ? err.message : 'Something went wrong';
}

export const api = {
  health: (): Promise<Health> => apiFetch('/health', healthSchema),
  flags: () =>
    apiFetch(
      '/flags',
      z.array(z.object({ name: z.string(), enabled: z.boolean(), isDefault: z.boolean() })),
    ),
};
